
import React, { useState, useEffect, useMemo } from 'react';
import { X, Save } from 'lucide-react';
import { UnionStatementRecord, Client } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  record: UnionStatementRecord | null;
  onSave: (record: UnionStatementRecord) => Promise<void>;
  allClients?: Client[];
}

const UnionRecordModal: React.FC<Props> = ({ isOpen, onClose, record, onSave, allClients = [] }) => {
  const [formData, setFormData] = useState<Partial<UnionStatementRecord>>({});
  const [clientQuery, setClientQuery] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  // 金額欄は入力中に文字列のまま保持し、保存時にのみ数値化する。
  // formData側で number|0 として保持すると、全消去時に parseInt('')||0 が
  // 即座に「0」へ戻り、ユーザーが0を消して入力し直せなくなるため。
  const [billingAmountInput, setBillingAmountInput] = useState<string>('');
  const [depositAmountInput, setDepositAmountInput] = useState<string>('');

  useEffect(() => {
    if (isOpen && record) {
      setFormData({ ...record });
      setBillingAmountInput(String(record.billing_amount ?? 0));
      setDepositAmountInput(String(record.deposit_amount ?? 0));
      // 初期表示は「コード 得意先名」の形式で表示
      const initLabel = record.client_code && record.client_code !== '未特定'
        ? `${record.client_code} ${record.client_name || ''}`.trim()
        : (record.client_name || record.client_abbreviation || '');
      setClientQuery(initLabel);
    }
  }, [isOpen, record]);

  // 得意先の検索結果（datalist 用）：得意先コードまたは名前でフィルタ。最大100件まで表示
  const clientOptions = useMemo(() => {
    if (!clientQuery) return allClients.slice(0, 100);
    const q = clientQuery.toLowerCase();
    return allClients
      .filter(c =>
        (c.client_code || '').toLowerCase().includes(q) ||
        (c.client_name || '').toLowerCase().includes(q) ||
        (c.client_kana || '').toLowerCase().includes(q)
      )
      .slice(0, 100);
  }, [clientQuery, allClients]);

  // 入力された値から得意先を特定（「コード 得意先名」形式 or コードのみ or 名前のみ）
  const resolveClientFromQuery = (query: string): Client | null => {
    if (!query) return null;
    const trimmed = query.trim();
    // 「コード 名前」形式の場合、最初のスペースまでをコードとして認識
    const firstSpace = trimmed.indexOf(' ');
    const codeCandidate = firstSpace >= 0 ? trimmed.substring(0, firstSpace) : trimmed;
    const byCode = allClients.find(c => c.client_code === codeCandidate);
    if (byCode) return byCode;
    // コード一致がなければ完全一致の名前で検索
    const byName = allClients.find(c => c.client_name === trimmed);
    if (byName) return byName;
    return null;
  };

  if (!isOpen || !record) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
        // 得意先入力欄に変更があれば、入力値から得意先を再特定して保存
        const resolvedClient = resolveClientFromQuery(clientQuery);
        const billingAmount = parseInt(billingAmountInput) || 0;
        const depositAmount = parseInt(depositAmountInput) || 0;
        const merged: UnionStatementRecord = {
            ...record,
            ...formData,
            billing_amount: billingAmount,
            deposit_amount: depositAmount,
            difference: billingAmount - depositAmount,
            ...(resolvedClient
                ? { client_code: resolvedClient.client_code, client_name: resolvedClient.client_name }
                : {})
        } as UnionStatementRecord;
        await onSave(merged);
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs">
      <div className="bg-white rounded shadow-2xl w-full max-w-md p-0 overflow-hidden animate-in zoom-in duration-200">
        <div className="px-5 py-3 border-b bg-gray-50 flex justify-between items-center">
            <h2 className="text-sm font-bold text-gray-800">明細レコードの編集</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">得意先（コードまたは名前で検索）</label>
            <input
              type="text"
              list="client-suggest-list"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder="得意先コードまたは名前を入力"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs font-bold"
            />
            <datalist id="client-suggest-list">
              {clientOptions.map(c => (
                <option key={c.client_code} value={`${c.client_code} ${c.client_name}`} />
              ))}
            </datalist>
            <p className="mt-1 text-[10px] text-gray-400">
              現在：{record.client_code === '未特定' ? '未特定' : `${record.client_code} ${record.client_name || ''}`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">区分</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs font-medium"
                >
                    <option value="">-</option>
                    <option value="手数料">手数料</option>
                    <option value="会費">会費</option>
                    <option value="調整金">調整金</option>
                    <option value="値引">値引</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">出荷</label>
                <select
                  value={formData.shipping || ''}
                  onChange={(e) => setFormData({...formData, shipping: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs font-medium"
                >
                    <option value="">-</option>
                    <option value="岩元">岩元</option>
                    <option value="高城">高城</option>
                    <option value="川辺">川辺</option>
                </select>
              </div>
          </div>

          <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">請求額 (明細記載)</label>
              <input
                type="number"
                value={billingAmountInput}
                onChange={(e) => setBillingAmountInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs text-right font-mono"
              />
          </div>

          <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">振込額</label>
              <input
                type="number"
                value={depositAmountInput}
                onChange={(e) => setDepositAmountInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs text-right font-mono"
              />
          </div>

          <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">備考</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs h-16"
                placeholder="備考入力"
              />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded text-gray-600 font-bold text-xs hover:bg-gray-50 transition">キャンセル</button>
            <button type="submit" disabled={isSaving} className="px-6 py-1.5 bg-blue-600 text-white rounded font-black text-xs hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 shadow-sm">
              <Save size={14} /> {isSaving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnionRecordModal;
